/**
 * DebugTaskQuery - Quick tool to see what tasks exist
 */

import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { Search } from 'lucide-react';

export default function DebugTaskQuery() {
  const [userId, setUserId] = useState('');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState('source_type');

  const queryTasks = async () => {
    if (!userId) {
      toast.error('Please enter a user ID');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('timeline_items')
        .select('id, title, source_type, source_id, is_subtask, parent_task_id, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setTasks(data || []);
      toast.success(`Found ${data?.length || 0} tasks`);
    } catch (error) {
      console.error('Error querying tasks:', error);
      toast.error('Failed to query tasks');
    } finally {
      setLoading(false);
    }
  };

  const grouped = tasks.reduce((acc, task) => {
    const key = task[groupBy] || 'unknown';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(task);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold mb-2">Debug Task Query</h3>
        <p className="text-sm text-slate-600 mb-4">
          View all tasks for a user grouped by source_type to debug what was created
        </p>
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          placeholder="User ID"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className="flex-1 px-3 py-2 border border-slate-300 rounded-lg"
        />
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg"
        >
          <option value="source_type">Group by Source Type</option>
          <option value="is_subtask">Group by Parent/Subtask</option>
        </select>
        <button
          onClick={queryTasks}
          disabled={loading}
          className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 flex items-center gap-2"
        >
          <Search className="h-4 w-4" />
          {loading ? 'Querying...' : 'Query Tasks'}
        </button>
      </div>

      {tasks.length > 0 && (
        <div className="space-y-4">
          <div className="text-sm font-semibold">
            Total: {tasks.length} tasks
          </div>

          {Object.entries(grouped).map(([key, groupTasks]) => (
            <div key={key} className="border border-slate-200 rounded-lg">
              <div className="bg-slate-50 px-4 py-2 font-semibold text-sm flex justify-between">
                <span>{key || 'unknown'}</span>
                <span className="text-slate-600">{groupTasks.length} tasks</span>
              </div>
              <div className="p-4 max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-slate-600 border-b">
                    <tr>
                      <th className="pb-2">Title</th>
                      <th className="pb-2">Source ID</th>
                      <th className="pb-2">Parent?</th>
                      <th className="pb-2">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {groupTasks.map((task) => (
                      <tr key={task.id} className="hover:bg-slate-50">
                        <td className="py-2 pr-4">{task.title}</td>
                        <td className="py-2 pr-4 text-xs font-mono text-slate-500">
                          {task.source_id?.substring(0, 30)}...
                        </td>
                        <td className="py-2 pr-4">
                          {task.is_subtask ? (
                            <span className="text-xs px-2 py-0.5 bg-violet-100 text-violet-700 rounded">subtask</span>
                          ) : task.parent_task_id ? (
                            <span className="text-xs px-2 py-0.5 bg-azure-100 text-azure-700 rounded">parent</span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">standalone</span>
                          )}
                        </td>
                        <td className="py-2 text-xs text-slate-500">
                          {new Date(task.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
